import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtErr, fmtDateTime } from "../../lib/api";
import { toast } from "sonner";
import { Users, MessageCircleHeart, ShieldCheck, RotateCcw, Search, Ban, Trash2, Eye } from "lucide-react";

const PLAN_LABELS = {
  admin: { label: "Amministratore", cls: "text-amber-300 border-amber-500/40" },
  sub: { label: "Abbonamento attivo", cls: "text-emerald-300 border-emerald-500/40" },
  gift: { label: "Giorni regalati", cls: "text-emerald-200 border-emerald-500/40" },
  trial: { label: "Prova gratuita", cls: "text-purple-200 border-[#4a2175]" },
  none: { label: "Nessun piano", cls: "text-purple-300/50 border-[#4a2175]" },
};

const VIRGINIA_LABELS = {
  disponibile: { label: "Disponibile", cls: "text-emerald-300" },
  utilizzata: { label: "Utilizzata", cls: "text-amber-300/80" },
  non_disponibile: { label: "Non abbonato", cls: "text-purple-300/40" },
};

const CHARGE_LABELS = {
  trial: "Inclusa nella prova gratuita",
  sub: "Inclusa nell'abbonamento",
  gift: "Inclusa nei giorni regalati",
  tester: "Inclusa nel periodo tester (concluso)",
  credits: "Scalata dai crediti acquistati",
  admin: "Amministratore (senza addebito)",
  storico: "Senza addebito registrato (storico)",
};

const DECK_LABELS = { "vera-sibilla": "Sibille", piacentine: "Piacentine", napoletane: "Napoletane", rune: "Rune", geomanzia: "Geomanzia", iching: "I Ching" };

const fmtDay = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const surnameOf = (n) => {
  const parts = (n || "").trim().split(/\s+/).filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1] : "";
};

const FILTER_OPTIONS = [
  ["all", "Tutti"],
  ["sub", "Abbonamento attivo"],
  ["gift", "Accesso regalato"],
  ["trial", "Prova gratuita"],
  ["none", "Nessun piano"],
];

const SORT_OPTIONS = [
  ["name", "Nome (A-Z)"],
  ["surname", "Cognome (A-Z)"],
  ["email", "Email (A-Z)"],
  ["created_desc", "Registrazione (recenti prima)"],
  ["created_asc", "Registrazione (meno recenti prima)"],
  ["expiry_asc", "Scadenza (più vicina)"],
  ["expiry_desc", "Scadenza (più lontana)"],
];

const EmailChanges = () => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && rows === null) {
      api.get("/admin/email-changes").then((r) => setRows(r.data)).catch(() => setRows([]));
    }
  };
  return (
    <div className="mystic-card p-6 mb-6" data-testid="admin-email-changes">
      <button onClick={toggle} data-testid="admin-email-changes-toggle" className="w-full flex items-center justify-between">
        <h3 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400">Storico cambi email</h3>
        <span className="text-xs text-purple-300/50">{open ? "Nascondi" : "Mostra"}</span>
      </button>
      {open && (
        rows === null ? (
          <p className="text-xs text-purple-300/50 mt-3">Caricamento…</p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-purple-300/50 mt-3" data-testid="admin-email-changes-empty">Nessun cambio email registrato.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {rows.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs border border-[#4a2175]/60 rounded-lg px-3 py-2" data-testid={`admin-email-change-${c.id}`}>
                <span className="text-purple-200/70">{c.old_email}</span>
                <span className="text-amber-400/70">→</span>
                <span className="text-amber-200">{c.new_email}</span>
                <span className="ml-auto text-purple-300/50">{fmtDateTime(c.changed_at)}{c.user?.name ? ` · ${c.user.name}` : ""}</span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

// Caricamento differito: la lista dei regali si apre su richiesta,
// così l'elenco utenti è la prima cosa visibile senza richieste extra.
const GiftList = () => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && rows === null) {
      api.get("/admin/gift-days").then((r) => setRows(r.data)).catch(() => setRows([]));
    }
  };
  return (
    <div className="mystic-card p-6 mb-6" data-testid="admin-gifts-section">
      <button onClick={toggle} data-testid="admin-gifts-toggle" className="w-full flex items-center justify-between">
        <h3 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400">Giorni gratuiti regalati</h3>
        <span className="text-xs text-purple-300/50">{open ? "Nascondi" : "Mostra"}</span>
      </button>
      <p className="text-xs text-purple-300/60 mt-1">
        Ogni giorno regalato include 3 consultazioni al giorno e 2 approfondimenti per consultazione. Se un regalo è ancora attivo, un nuovo regalo prolunga la scadenza.
      </p>
      {open && (
        rows === null ? (
          <p className="text-xs text-purple-300/50 mt-3">Caricamento…</p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-purple-300/50 italic mt-3" data-testid="admin-gifts-empty">Nessun regalo ancora effettuato.</p>
        ) : (
        <div className="space-y-2 mt-3">
          {rows.map((g) => (
            <div key={g.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs border border-[#4a2175]/60 rounded-lg px-3 py-2" data-testid={`admin-gift-${g.id}`}>
              <span className="text-amber-200 font-medium">{g.user_name || g.user_email}</span>
              <span className="text-purple-200/70">{g.months ? `${g.months} ${g.months === 1 ? "mese" : "mesi"}` : `${g.days} ${g.days === 1 ? "giorno" : "giorni"}`}</span>
              <span className="text-purple-300/50">dal {fmtDateTime(g.start)}</span>
              <span className="text-purple-300/50">al {fmtDateTime(g.end)}</span>
              <span className={`ml-auto text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${g.active ? "border-emerald-500/50 text-emerald-300" : "border-purple-500/40 text-purple-300/50"}`} data-testid={`admin-gift-status-${g.id}`}>
                {g.active ? "Attivo" : "Scaduto"}
              </span>
            </div>
          ))}
        </div>
        )
      )}
    </div>
  );
};

const UserDetail = ({ uid, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${uid}/detail`).then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [uid]);

  if (!data) return <div className="mystic-card p-6 text-center text-purple-300/50 text-sm" data-testid="admin-user-detail-loading">Caricamento scheda…</div>;
  const { user, plan, stats } = data;
  const planInfo = PLAN_LABELS[plan] || PLAN_LABELS.none;

  return (
    <div className="mystic-card p-6 space-y-6" data-testid="admin-user-detail">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-xl text-amber-100">{user.name}</p>
          <p className="text-xs text-purple-300/60">{user.email}</p>
          <p className="text-[11px] text-purple-300/40 mt-1">
            Registrato il {user.created_at ? fmtDateTime(user.created_at) : "—"}
            {stats.last_activity ? ` · Ultima attività: ${fmtDateTime(stats.last_activity)}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-wider border rounded-full px-3 py-1 ${planInfo.cls}`}>{planInfo.label}</span>
          <button onClick={onClose} data-testid="admin-user-detail-close" className="text-xs text-purple-300/60 border border-[#4a2175] rounded-full px-3 py-1.5 hover:bg-purple-500/10">Chiudi</button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        {[
          ["Letture totali", stats.readings_total],
          ["Approfondimenti", stats.followups_total],
          ["Geomanzie", stats.geomancy_total],
          ["Ultimi 7 giorni", stats.last_7d],
        ].map(([label, val]) => (
          <div key={label} className="border border-[#4a2175]/60 rounded-xl p-3">
            <p className="font-display text-2xl text-amber-200">{val}</p>
            <p className="text-[10px] uppercase tracking-wider text-purple-300/50 mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 text-xs text-purple-200/70">
        <div className="border border-[#4a2175]/60 rounded-xl p-4">
          <p className="eyebrow mb-2">Consumo per tipo di addebito</p>
          {Object.keys(stats.by_charge).length === 0 ? <p className="text-purple-300/40 italic">Nessuna consultazione.</p> :
            Object.entries(stats.by_charge).map(([k, v]) => (
              <p key={k} className="flex justify-between py-0.5"><span>{CHARGE_LABELS[k] || k}</span><span className="text-amber-200">{v}</span></p>
            ))}
          <p className="flex justify-between py-0.5 mt-2 pt-2 border-t border-[#4a2175]/40"><span>Crediti residui</span><span className="text-amber-200">{user.consult_credits || 0}</span></p>
        </div>
        <div className="border border-[#4a2175]/60 rounded-xl p-4">
          <p className="eyebrow mb-2">Per metodo / piano</p>
          {Object.keys(stats.by_deck).length === 0 && <p className="text-purple-300/40 italic">Nessuna consultazione.</p>}
          {Object.entries(stats.by_deck).map(([k, v]) => (
            <p key={k} className="flex justify-between py-0.5"><span>{DECK_LABELS[k] || k}</span><span className="text-amber-200">{v}</span></p>
          ))}
          {plan === "sub" && <p className="mt-2 pt-2 border-t border-[#4a2175]/40">Abbonamento fino al: <span className="text-amber-200">{data.access_until ? fmtDateTime(data.access_until) : "—"}</span></p>}
          {plan === "gift" && <p className="mt-2 pt-2 border-t border-[#4a2175]/40">Giorni regalati fino al: <span className="text-amber-200">{data.access_until ? fmtDateTime(data.access_until) : "—"}</span></p>}
          {plan === "trial" && <p className="mt-2 pt-2 border-t border-[#4a2175]/40">Prova fino al: <span className="text-amber-200">{data.trial_ends_at ? fmtDateTime(data.trial_ends_at) : "—"}</span></p>}
        </div>
      </div>
      <div className="border border-[#4a2175]/60 rounded-xl p-4" data-testid="admin-user-credit-moves">
        <p className="eyebrow mb-2">Storico crediti e abbonamenti</p>
        {(data.credit_movements || []).length === 0 ? (
          <p className="text-purple-300/40 italic">Nessun movimento registrato.</p>
        ) : (
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {data.credit_movements.map((m) => (
              <p key={m.id} className="flex flex-wrap justify-between gap-x-2 py-0.5" data-testid={`admin-credit-move-${m.id}`}>
                <span>
                  {m.delta > 0 && <span className="text-emerald-300 font-semibold">+{m.delta} </span>}
                  {m.delta < 0 && <span className="text-rose-300 font-semibold">{m.delta} </span>}
                  {m.note || m.kind}
                </span>
                <span className="text-purple-300/50">{fmtDateTime(m.at)} · saldo {m.balance_after}</span>
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DeletedUsers = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && data === null) {
      api.get("/admin/deleted-users").then((r) => setData(r.data)).catch(() => setData({ payments: [], play_purchases: [], gifts: [], total_residual: 0 }));
    }
  };
  return (
    <div className="mystic-card p-6 mt-6" data-testid="admin-deleted-section">
      <button onClick={toggle} data-testid="admin-deleted-toggle" className="w-full flex items-center justify-between">
        <h3 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400">Account eliminati — dati residui</h3>
        <span className="text-xs text-purple-300/50">{open ? "Nascondi" : "Mostra"}</span>
      </button>
      {open && (
        data === null ? (
          <p className="text-xs text-purple-300/50 mt-3">Caricamento…</p>
        ) : data.total_residual === 0 ? (
          <p className="text-xs text-purple-300/50 mt-3" data-testid="admin-deleted-empty">Nessun dato residuo: gli account eliminati non hanno lasciato record.</p>
        ) : (
          <div className="mt-3 space-y-2">
            <p className="text-[11px] text-purple-300/50">
              Per legge i record fiscali dei pagamenti sono conservati in forma anonima (art. 2220 c.c.). Le email sono mascherate per privacy.
            </p>
            {data.payments.map((p, i) => (
              <div key={`p${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs border border-[#4a2175]/60 rounded-lg px-3 py-2" data-testid={`admin-deleted-payment-${i}`}>
                <span className="text-purple-200/70">Stripe · {p.lookup_key || "pagamento"}</span>
                <span className="text-amber-200">{p.amount != null ? `${(p.amount / 100).toFixed(2)} ${(p.currency || "eur").toUpperCase()}` : "—"}</span>
                <span className="text-purple-300/50">{p.payment_status || p.status}</span>
                <span className="ml-auto text-purple-300/50">{fmtDateTime(p.created_at)}</span>
              </div>
            ))}
            {data.play_purchases.map((p, i) => (
              <div key={`g${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs border border-[#4a2175]/60 rounded-lg px-3 py-2" data-testid={`admin-deleted-play-${i}`}>
                <span className="text-purple-200/70">Google Play · {p.product_id || p.type || "acquisto"}</span>
                <span className="ml-auto text-purple-300/50">{fmtDateTime(p.created_at)}</span>
              </div>
            ))}
            {data.gifts.map((g, i) => (
              <div key={`r${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs border border-[#4a2175]/60 rounded-lg px-3 py-2" data-testid={`admin-deleted-gift-${i}`}>
                <span className="text-purple-200/70">Regalo · {g.days} {g.days === 1 ? "giorno" : "giorni"}</span>
                <span className="text-purple-300/50">{g.user_email}</span>
                <span className="ml-auto text-purple-300/50">{g.start ? `dal ${fmtDay(g.start)} al ${fmtDay(g.end)}` : fmtDateTime(g.created_at)}</span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState("");
  const [query, setQuery] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [sortKey, setSortKey] = useState("name");
  const [detailId, setDetailId] = useState(null);
  const [stats, setStats] = useState(null);
  const [giftInput, setGiftInput] = useState({});
  const [creditInput, setCreditInput] = useState({});

  const load = useCallback(() => {
    api.get("/admin/users").then((r) => { setUsers(r.data.users); setStats(r.data.stats); }).catch(() => setUsers([]));
  }, []);
  useEffect(load, [load]);

  const giftDays = async (uid, months = 0) => {
    const days = months ? 0 : parseInt(giftInput[uid] || "0", 10);
    if (!months && (!days || days < 1)) { toast.error("Inserisci il numero di giorni da regalare."); return; }
    const what = months ? "1 mese di accesso (fino allo stesso giorno del mese successivo)" : `${days} giorni di accesso`;
    if (!window.confirm(`Regalare ${what} gratuito a questo utente? (3 consultazioni al giorno, 2 approfondimenti per consultazione, max 6 approfondimenti al giorno).`)) return;
    setBusy(`${uid}:gift`);
    try {
      const r = await api.post(`/admin/users/${uid}/gift-days`, { days, months });
      toast.success(`Regalo attivo fino al ${fmtDateTime(r.data.access_until)}.`);
      setGiftInput((s) => ({ ...s, [uid]: "" }));
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  const sendVerificationAll = async () => {
    if (!window.confirm("Inviare l'email di verifica a TUTTI gli utenti non ancora verificati? Ognuno riceverà un link valido 24 ore per confermare il proprio indirizzo.")) return;
    setBusy("verify-all");
    try {
      const r = await api.post("/admin/users/send-verification-all");
      toast.success(`Invio completato: ${r.data.sent} email inviate su ${r.data.total} utenti non verificati${r.data.failed ? ` (${r.data.failed} falliti)` : ""}.`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  const grantCredits = async (uid) => {
    const amount = parseInt(creditInput[uid] || "0", 10);
    if (!amount || amount < 1 || amount > 10) { toast.error("Inserisci da 1 a 10 crediti da assegnare."); return; }
    if (!window.confirm(`Assegnare ${amount} crediti a questo utente? Saranno utilizzabili quando vuole entro 30 giorni da oggi, poi scadranno. L'assegnazione resta registrata nello storico.`)) return;
    setBusy(`${uid}:credits`);
    try {
      const r = await api.post(`/admin/users/${uid}/credits`, { amount });
      if (r.data.duplicate) toast.info("Assegnazione identica appena registrata: nessun duplicato creato.");
      else toast.success(`${amount} crediti assegnati, validi fino al ${new Date(r.data.expires_at).toLocaleDateString("it-IT")}.`);
      setCreditInput((s) => ({ ...s, [uid]: "" }));
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  const revokeCredits = async (uid) => {
    if (!window.confirm("Annullare SUBITO tutti i crediti regalati di questo utente? L'annullamento è immediato e resta registrato nello storico. I crediti acquistati non vengono toccati.")) return;
    setBusy(`${uid}:credits-revoke`);
    try {
      const r = await api.post(`/admin/users/${uid}/credits/revoke`);
      toast.success(r.data.revoked > 0 ? `${r.data.revoked} crediti regalati annullati con effetto immediato.` : "Nessun credito regalato attivo da annullare.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  const act = async (uid, action) => {
    const msgs = {
      grant: "Assegnare un abbonamento regalato a questo utente? Potrai scegliere la durata (7, 14 o 30 giorni): avrà gli stessi benefici dell'abbonamento e riceverà una notifica push.",
      revoke: "Rimuovere l'abbonamento di questo utente?",
      virginia: "Riabilitare la domanda a Virginia per il periodo corrente?",
      suspend: "Sospendere questo account? Non potrà più accedere al servizio finché non lo riattivi. I dati restano conservati.",
      unsuspend: "Riattivare questo account?",
      delete: "ELIMINARE DEFINITIVAMENTE questo account? Verranno cancellati tutti i contenuti (letture, diario, prenotazioni, recensioni). I record fiscali dei pagamenti saranno conservati in forma anonima per obblighi di legge. L'operazione è IRREVERSIBILE.",
    };
    if (!window.confirm(msgs[action])) return;
    setBusy(`${uid}:${action}`);
    try {
      if (action === "virginia") await api.post(`/admin/users/${uid}/virginia-reset`);
      else if (action === "suspend" || action === "unsuspend") await api.post(`/admin/users/${uid}/${action}`);
      else if (action === "delete") await api.delete(`/admin/users/${uid}`);
      else if (action === "grant") {
        const days = window.prompt("Durata dell'abbonamento regalato, in giorni (7, 14 o 30):", "30");
        if (days === null) return;
        const d = parseInt(days, 10);
        if (![7, 14, 30].includes(d)) {
          toast.error("Durata non valida: scegli 7, 14 o 30 giorni.");
          return;
        }
        const r = await api.post(`/admin/users/${uid}/subscription`, { action, days: d });
        if (r.data.duplicate) toast.info("Regalo già assegnato un attimo fa: nessun duplicato creato.");
        else toast.success(`Abbonamento di ${d} giorni attivo fino al ${fmtDay(r.data.access_until)} · push ${r.data.push ? "inviata" : "non recapitata (tracciata nei log)"}.`);
      }
      else await api.post(`/admin/users/${uid}/subscription`, { action });
      if (action !== "grant") toast.success("Operazione completata.");
      if (action === "delete" && detailId === uid) setDetailId(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  const rows = useMemo(() => {
    if (!users) return [];
    return users.map((u) => ({ user: u, email: u.email, name: u.name || "" }));
  }, [users]);

  const haystack = (r) => {
    const u = r.user;
    return [
      r.name, r.email,
      (PLAN_LABELS[u.plan] || PLAN_LABELS.none).label,
      fmtDay(u.created_at),
      u.access_until ? fmtDay(u.access_until) : "",
      u.preferred_method ? (DECK_LABELS[u.preferred_method] || u.preferred_method) : "",
      String(u.credits),
      String(u.readings_total),
    ].join(" ").toLowerCase();
  };

  const expiryOf = (r) => (r.user && (r.user.access_until || r.user.trial_ends_at)) || null;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cmpText = (a, b) => (a || "").localeCompare(b || "", "it");
    const list = rows.filter((r) => {
      if (planFilter !== "all" && r.user.plan !== planFilter) return false;
      return !q || haystack(r).includes(q);
    });
    return list.sort((a, b) => {
      if (sortKey === "surname") return (a.name ? 0 : 1) - (b.name ? 0 : 1) || cmpText(surnameOf(a.name), surnameOf(b.name)) || cmpText(a.name, b.name);
      if (sortKey === "email") return cmpText(a.email, b.email);
      if (sortKey === "created_asc") return (a.user ? 0 : 1) - (b.user ? 0 : 1) || cmpText(a.user?.created_at, b.user?.created_at);
      if (sortKey === "created_desc") return (a.user ? 0 : 1) - (b.user ? 0 : 1) || cmpText(b.user?.created_at, a.user?.created_at);
      if (sortKey === "expiry_asc" || sortKey === "expiry_desc") {
        const ea = expiryOf(a);
        const eb = expiryOf(b);
        return ((ea ? 0 : 1) - (eb ? 0 : 1)) || (sortKey === "expiry_asc" ? cmpText(ea, eb) : cmpText(eb, ea));
      }
      return (a.name ? 0 : 1) - (b.name ? 0 : 1) || cmpText(a.name, b.name) || cmpText(a.email, b.email);
    });
  }, [rows, query, planFilter, sortKey]);

  return (
    <div data-testid="admin-users-section">
      <h2 className="font-display text-2xl text-amber-100 mb-2">Utenti</h2>
      <p className="text-xs text-purple-300/60 mb-6">
        Tutti gli account registrati, con piano, utilizzi, consumi e azioni di gestione.
      </p>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="mystic-card p-4 text-center" data-testid="admin-users-total">
          <p className="font-display text-2xl text-amber-200">{rows.length}</p>
          <p className="text-[10px] uppercase tracking-wider text-purple-300/50 mt-1">Iscritti totali</p>
        </div>
        <div className="mystic-card p-4 text-center" data-testid="admin-users-subs-active">
          <p className="font-display text-2xl text-amber-200">{rows.filter((r) => r.user && r.user.plan === "sub").length}</p>
          <p className="text-[10px] uppercase tracking-wider text-purple-300/50 mt-1">Abbonati attivi</p>
        </div>
      </div>

      <div className="mystic-card p-5 mb-6" data-testid="admin-controls">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-purple-300/40 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca in tutti i campi (nome, email, piano, date, metodo…)"
              data-testid="admin-users-search"
              className="input-dark pl-10"
            />
          </div>
          <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} data-testid="admin-users-filter"
            className="input-dark w-auto text-xs py-2">
            {FILTER_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} data-testid="admin-users-sort"
            className="input-dark w-auto text-xs py-2">
            {SORT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span className="text-[10px] uppercase tracking-wider border border-[#4a2175] rounded-full px-3 py-1.5 text-purple-300/60" data-testid="admin-users-count">
            {rows.length ? `${shown.length}/${rows.length} voci` : "…"}
          </span>
          {stats && (
            <span className="text-[10px] uppercase tracking-wider border border-[#4a2175] rounded-full px-3 py-1.5 text-purple-300/60" data-testid="admin-users-stats">
              {stats.verificati} verificati · {stats.non_verificati} non verificati · {stats.completi} completi · {stats.trial_avviati} trial ({stats.trial_usati} usati)
            </span>
          )}
          <button
            onClick={sendVerificationAll}
            disabled={busy === "verify-all"}
            data-testid="admin-send-verification-all-btn"
            className="text-[10px] uppercase tracking-wider border border-amber-500/40 rounded-full px-3 py-1.5 text-amber-200/80 hover:bg-amber-500/10 transition-colors disabled:opacity-50"
          >
            {busy === "verify-all" ? "Invio in corso…" : "Invia verifica a tutti i non verificati"}
          </button>
        </div>
      </div>

      {users === null ? (
        <div className="mystic-card p-10 text-center" data-testid="admin-users-loading">
          <p className="text-purple-200/60 text-sm">Caricamento utenti…</p>
        </div>
      ) : shown.length === 0 ? (
        <div className="mystic-card p-10 text-center" data-testid="admin-users-empty">
          <Users className="w-8 h-8 text-amber-400/50 mx-auto mb-3" />
          <p className="text-purple-200/60 text-sm">{query || planFilter !== "all" ? "Nessuna voce corrisponde ai filtri." : "Nessuna voce presente."}</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-users-list">
          {shown.map((r) => {
            const u = r.user;
            const plan = u ? (PLAN_LABELS[u.plan] || PLAN_LABELS.none) : null;
            const vq = u ? (VIRGINIA_LABELS[u.virginia_status] || VIRGINIA_LABELS.non_disponibile) : null;
            return (
              <div key={r.email} className="mystic-card p-6" data-testid={`admin-row-${r.email}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <p className="font-display text-lg text-amber-100 flex items-center gap-2" data-testid={`admin-row-name-${r.email}`}>
                      {u ? (u.name || "—") : r.email}
                      {u?.role === "admin" && <ShieldCheck className="w-4 h-4 text-amber-400" />}
                      {u?.disabled && <span className="text-[10px] uppercase tracking-wider border border-red-500/50 text-red-300 rounded-full px-2 py-0.5" data-testid={`admin-user-suspended-${u.id}`}>Sospeso</span>}
                    </p>
                    {u && <p className="text-sm text-amber-200/80" data-testid={`admin-row-email-${r.email}`}>{u.email}</p>}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className={`text-[10px] uppercase tracking-wider border rounded-full px-3 py-1 ${u ? plan.cls : "text-purple-300/50 border-[#4a2175]"}`} data-testid={`admin-row-plan-${r.email}`}>
                        {plan.label}
                      </span>
                      {u && !u.email_verified && (
                        <span className="text-[10px] uppercase tracking-wider border border-amber-500/40 text-amber-300/80 rounded-full px-3 py-1" data-testid={`admin-user-unverified-${u.id}`}>Email non verificata</span>
                      )}
                      {u && (
                        <span className="text-[10px] uppercase tracking-wider border rounded-full px-3 py-1 text-purple-200/80 border-[#4a2175]" data-testid={`admin-row-platform-${r.email}`}>
                          {u.platform_label || "—"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-xs text-purple-200/70">
                  <p>Registrato il: <span className="text-amber-200">{u ? fmtDay(u.created_at) : "—"}</span></p>
                  <p>Consultazioni oggi:{" "}
                    <span className="text-amber-200">
                      {u ? `${u.consults_used_today}${u.consults_limit != null ? `/${u.consults_limit}` : ""}` : "—"}
                    </span>
                  </p>
                  <p>Media utilizzi: <span className="text-amber-200" data-testid={u ? `admin-user-avg-${u.id}` : undefined}>{u ? `${u.consult_avg_per_day ?? 0}/giorno` : "—"}</span></p>
                  <p>Metodo preferito: <span className="text-amber-200" data-testid={u ? `admin-user-method-${u.id}` : undefined}>{u && u.preferred_method ? (DECK_LABELS[u.preferred_method] || u.preferred_method) : "—"}</span></p>
                  {u?.plan === "sub" && (
                    <p>Abbonamento fino al: <span className="text-amber-200">{u.access_until ? fmtDay(u.access_until) : "—"}</span></p>
                  )}
                  {u?.plan === "trial" && (
                    <p>Prova fino al: <span className="text-amber-200">{u.trial_ends_at ? fmtDay(u.trial_ends_at) : "—"}</span></p>
                  )}
                  {u?.plan === "gift" && (
                    <p data-testid={`admin-user-giftinfo-${r.email}`}>Giorni regalati fino al: <span className="text-emerald-200">{u.access_until ? fmtDay(u.access_until) : "—"}</span></p>
                  )}
                  <p>Letture totali: <span className="text-amber-200">{u ? u.readings_total : "—"}</span></p>
                  <p>Crediti consultazioni: <span className="text-amber-200">{u ? u.credits : "—"}</span></p>
                  {u && (
                    <p className="flex items-center gap-1.5">
                      <MessageCircleHeart className="w-3.5 h-3.5 text-amber-400/70" />
                      Domanda a Virginia: <span className={vq.cls} data-testid={`admin-user-virginia-${u.id}`}>{vq.label}</span>
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3 mt-5 pt-4 border-t border-[#4a2175]/50">
                  {u && (
                    <button
                      onClick={() => setDetailId(detailId === u.id ? null : u.id)}
                      data-testid={`admin-user-detail-btn-${u.id}`}
                      className="text-xs text-amber-200 border border-amber-500/40 rounded-full px-4 py-1.5 hover:bg-amber-500/10 transition-colors inline-flex items-center gap-1.5"
                    >
                      <Eye className="w-3.5 h-3.5" /> {detailId === u.id ? "Nascondi scheda" : "Scheda e consumi"}
                    </button>
                  )}
                  {u.role !== "admin" && (
                    <>
                      <button onClick={() => act(u.id, "grant")} disabled={busy === `${u.id}:grant`} data-testid={`admin-user-grant-${u.id}`}
                        className="text-xs text-emerald-200 border border-emerald-500/40 rounded-full px-4 py-1.5 hover:bg-emerald-500/10 transition-colors disabled:opacity-50">
                        Assegna abbonamento
                      </button>
                      <div className="flex items-center gap-2" data-testid={`admin-user-gift-box-${u.id}`}>
                        <input type="number" min="1" max="365" placeholder="giorni"
                          value={giftInput[u.id] || ""}
                          onChange={(e) => setGiftInput((s) => ({ ...s, [u.id]: e.target.value }))}
                          className="input-dark w-20 py-1.5 text-xs" data-testid={`admin-user-gift-days-${u.id}`} />
                        <button onClick={() => giftDays(u.id)} disabled={busy === `${u.id}:gift`} data-testid={`admin-user-gift-${u.id}`}
                          className="text-xs text-purple-200 border border-[#4a2175] rounded-full px-4 py-1.5 hover:bg-purple-500/10 transition-colors disabled:opacity-50">
                          Regala giorni
                        </button>
                      </div>
                      <div className="flex items-center gap-2" data-testid={`admin-user-credits-box-${u.id}`}>
                        <input type="number" min="1" max="10" placeholder="crediti"
                          value={creditInput[u.id] || ""}
                          onChange={(e) => setCreditInput((s) => ({ ...s, [u.id]: e.target.value }))}
                          className="input-dark w-20 py-1.5 text-xs" data-testid={`admin-user-credits-input-${u.id}`} />
                        <button onClick={() => grantCredits(u.id)} disabled={busy === `${u.id}:credits`} data-testid={`admin-user-credits-${u.id}`}
                          className="text-xs text-amber-200 border border-amber-500/40 rounded-full px-4 py-1.5 hover:bg-amber-500/10 transition-colors disabled:opacity-50">
                          Assegna crediti
                        </button>
                        <button onClick={() => revokeCredits(u.id)} disabled={busy === `${u.id}:credits-revoke`} data-testid={`admin-user-credits-revoke-${u.id}`}
                          className="text-xs text-red-300/80 border border-red-500/40 rounded-full px-4 py-1.5 hover:bg-red-500/10 transition-colors disabled:opacity-50">
                          Annulla crediti
                        </button>
                      </div>
                      {u?.plan === "sub" && (
                        <button onClick={() => act(u.id, "revoke")} disabled={busy === `${u.id}:revoke`} data-testid={`admin-user-revoke-${u.id}`}
                          className="text-xs text-red-300/80 border border-red-500/40 rounded-full px-4 py-1.5 hover:bg-red-500/10 transition-colors disabled:opacity-50">
                          Annulla abbonamento
                        </button>
                      )}
                      {u?.virginia_status === "utilizzata" && (
                        <button onClick={() => act(u.id, "virginia")} disabled={busy === `${u.id}:virginia`} data-testid={`admin-user-virginia-reset-${u.id}`}
                          className="text-xs text-amber-200 border border-amber-500/40 rounded-full px-4 py-1.5 hover:bg-amber-500/10 transition-colors disabled:opacity-50 flex items-center gap-1.5">
                          <RotateCcw className="w-3.5 h-3.5" /> Riabilita domanda Virginia
                        </button>
                      )}
                      {u && (u.disabled ? (
                        <button onClick={() => act(u.id, "unsuspend")} disabled={busy === `${u.id}:unsuspend`} data-testid={`admin-user-unsuspend-${u.id}`}
                          className="text-xs text-emerald-200 border border-emerald-500/40 rounded-full px-4 py-1.5 hover:bg-emerald-500/10 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                          <RotateCcw className="w-3.5 h-3.5" /> Riattiva account
                        </button>
                      ) : (
                        <button onClick={() => act(u.id, "suspend")} disabled={busy === `${u.id}:suspend`} data-testid={`admin-user-suspend-${u.id}`}
                          className="text-xs text-red-300 border border-red-500/50 rounded-full px-4 py-1.5 hover:bg-red-500/10 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                          <Ban className="w-3.5 h-3.5" /> Sospendi account
                        </button>
                      ))}
                      {u && (
                        <button onClick={() => act(u.id, "delete")} disabled={busy === `${u.id}:delete`} data-testid={`admin-user-delete-${u.id}`}
                          className="text-xs text-red-300/70 border border-red-500/30 rounded-full px-4 py-1.5 hover:bg-red-500/10 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                          <Trash2 className="w-3.5 h-3.5" /> Elimina
                        </button>
                      )}
                    </>
                  )}
                </div>

                {u && detailId === u.id && <div className="mt-5"><UserDetail uid={u.id} onClose={() => setDetailId(null)} /></div>}
              </div>
            );
          })}
        </div>
      )}

      <EmailChanges />
      <GiftList />
      <DeletedUsers />
    </div>
  );
}
